import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  FileText,
  Edit,
  Trash2,
  ChevronRight,
  RefreshCw,
  Activity,
  AlertCircle,
  Brain,
  LogOut,
  Building2,
  Calendar,
  DollarSign,
  Plus,
  X,
  Loader2,
  History,
  Settings,
  Droplets,
  Send
} from "lucide-react";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import CampaignAlertsPanel from "@/components/CampaignAlertsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700 border-blue-200", icon: FileText },
  sent_to_suppliers: { label: "Sent to Suppliers", color: "bg-purple-100 text-purple-700 border-purple-200", icon: Truck },
  quotes_received: { label: "Quotes Received", color: "bg-amber-100 text-amber-700 border-amber-200", icon: DollarSign },
  priced: { label: "Priced", color: "bg-cyan-100 text-cyan-700 border-cyan-200", icon: DollarSign },
  approved: { label: "Approved", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: Package },
  shipped: { label: "Shipped", color: "bg-orange-100 text-orange-700 border-orange-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle }
};

// Stats Card - Light Theme
const StatsCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    purple: "bg-violet-50 text-violet-700",
    red: "bg-red-50 text-red-700"
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <p className="text-3xl font-bold text-slate-900 font-manrope">{value}</p>
      <p className="text-slate-500 text-sm mt-1">{label}</p>
    </div>
  );
};

// Request Card - Light Theme
const RequestCard = ({ request, onView, onEdit, onCancel }) => {
  const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.submitted;
  const StatusIcon = status.icon;
  
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-200 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-slate-400 text-sm font-mono">#{request.id.substring(0, 8)}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
              {status.label}
            </span>
          </div>
          <p className="text-slate-900 font-semibold">
            {request.items?.length || 0} item{request.items?.length !== 1 ? "s" : ""} requested
          </p>
        </div>
        <StatusIcon className={`w-5 h-5 ${status.color.split(" ")[1]}`} strokeWidth={1.5} />
      </div>
      
      <div className="space-y-2 mb-4">
        {request.items?.slice(0, 2).map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <span className="text-slate-700">{item.item_name}</span>
            <span className="text-slate-500">Qty: {item.quantity}</span>
          </div>
        ))}
        {request.items?.length > 2 && (
          <p className="text-slate-400 text-sm">+{request.items.length - 2} more items</p>
        )}
      </div>
      
      {request.quotes?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
          <p className="text-emerald-700 text-sm font-medium">
            {request.quotes.length} quote{request.quotes.length !== 1 ? "s" : ""} received
          </p>
        </div>
      )}
      
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <span className="text-slate-400 text-xs">
          {new Date(request.created_at).toLocaleDateString()}
        </span>
        <div className="flex items-center gap-2">
          {["submitted", "sent_to_suppliers"].includes(request.status) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => onEdit(request)} className="text-slate-500 hover:text-blue-700 hover:bg-blue-50">
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onCancel(request)} className="text-slate-500 hover:text-red-600 hover:bg-red-50">
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => onView(request)} className="bg-blue-700 hover:bg-blue-800 text-white">
            View <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function HospitalDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hospital, setHospital] = useState(null);
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("requests");
  const [diDeployments, setDiDeployments] = useState([]);
  const [diRequests, setDiRequests] = useState([]);
  const [diFormOpen, setDiFormOpen] = useState(false);
  const [diForm, setDiForm] = useState({
    department: "", service_type: "exchange", tank_size: "1.6 cu. ft.",
    tank_type: "Standard DI", requested_date: "", notes: ""
  });
  const [diSubmitting, setDiSubmitting] = useState(false);

  const token = localStorage.getItem("hospital_token");

  useEffect(() => {
    if (!token) {
      navigate("/hospital/backorders");
      return;
    }
    fetchDashboard();
  }, [token, navigate]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hospitals/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("hospital_token");
          localStorage.removeItem("hospital_info");
          navigate("/hospital/backorders");
          return;
        }
        throw new Error("Failed to fetch dashboard");
      }
      
      const data = await res.json();
      setHospital(data.hospital);
      setStats(data.stats);
      setRequests(data.requests);
      setActivityLog(data.activity_log);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("hospital_token");
    localStorage.removeItem("hospital_info");
    navigate("/hospital/backorders");
    toast.success("Logged out successfully");
  };

  // DI Tank functions
  const fetchDiData = async () => {
    const hdrs = { Authorization: `Bearer ${token}` };
    try {
      const [depRes, reqRes] = await Promise.all([
        fetch(`${API_URL}/api/di-tanks/hospital/deployments`, { headers: hdrs }),
        fetch(`${API_URL}/api/di-tanks/hospital/exchange-requests`, { headers: hdrs }),
      ]);
      if (depRes.ok) setDiDeployments(await depRes.json());
      if (reqRes.ok) setDiRequests(await reqRes.json());
    } catch { /* silent */ }
  };

  useEffect(() => { if (activeTab === "di-tanks" && token) fetchDiData(); }, [activeTab]);

  const handleDiSubmit = async () => {
    if (!diForm.department || !diForm.requested_date) {
      toast.error("Please fill department and requested date");
      return;
    }
    setDiSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/di-tanks/hospital/exchange-requests`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...diForm, company_name: hospital?.hospital_name || "" }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Submit failed"); }
      toast.success("Exchange request submitted successfully");
      setDiFormOpen(false);
      setDiForm({ department: "", service_type: "exchange", tank_size: "1.6 cu. ft.", tank_type: "Standard DI", requested_date: "", notes: "" });
      fetchDiData();
    } catch (err) { toast.error(err.message); }
    finally { setDiSubmitting(false); }
  };

  const handleCancelRequest = async () => {
    if (!cancellingRequest) return;
    
    try {
      const res = await fetch(`${API_URL}/api/hospitals/requests/${cancellingRequest.id}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to cancel");
      }
      
      toast.success("Request cancelled");
      setCancellingRequest(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredRequests = statusFilter === "all" 
    ? requests 
    : requests.filter(r => r.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-700 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-slate-900 font-bold text-lg font-manrope">AskIsaac</h1>
                <p className="text-slate-400 text-xs">Hospital Dashboard | </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-slate-900 font-medium">{hospital?.hospital_name}</p>
                <p className="text-slate-500 text-sm">{hospital?.email}</p>
              </div>
              <TelegramConnect token={token} userType="hospital" />
              <Button variant="outline" onClick={handleLogout} className="text-slate-600 border-slate-200 hover:bg-slate-50">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Telegram Invite Banner - shows once per session for non-connected users */}
        <TelegramInviteBanner 
          token={token} 
          userType="hospital" 
          userName={hospital?.contact_name?.split(' ')[0]}
        />
        
        {/* Welcome Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-blue-700" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-manrope">Welcome back, {hospital?.contact_name}</h2>
              <p className="text-slate-400">{hospital?.hospital_name} • Track your backorder requests and fulfillment status</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatsCard icon={Package} label="Total Requests" value={stats.total_requests || 0} color="blue" />
          <StatsCard icon={Clock} label="Pending" value={stats.pending || 0} color="amber" />
          <StatsCard icon={DollarSign} label="Quoted" value={stats.quoted || 0} color="purple" />
          <StatsCard icon={Truck} label="In Progress" value={stats.in_progress || 0} color="green" />
          <StatsCard icon={CheckCircle2} label="Completed" value={stats.completed || 0} color="green" />
        </div>

        {/* Supplier Product Alerts */}
        <div className="mb-8">
          <CampaignAlertsPanel 
            token={localStorage.getItem("token")} 
            userType="hospital"
            maxAlerts={3}
            onAlertClick={(alert) => {
              toast.info(`Contact ${alert.supplier_name} at ${alert.supplier_email} to order ${alert.product_name}`);
            }}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "requests"
                ? "bg-blue-700 text-white"
                : "text-slate-600 hover:text-blue-700 hover:bg-blue-50"
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            My Requests
          </button>
          <button
            onClick={() => setActiveTab("activity")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "activity"
                ? "bg-blue-700 text-white"
                : "text-slate-600 hover:text-blue-700 hover:bg-blue-50"
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Activity Log
          </button>
          <button
            onClick={() => setActiveTab("di-tanks")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "di-tanks"
                ? "bg-cyan-700 text-white"
                : "text-slate-600 hover:text-cyan-700 hover:bg-cyan-50"
            }`}
            data-testid="hospital-di-tanks-tab"
          >
            <Droplets className="w-4 h-4 inline mr-2" />
            DI Tanks
          </button>
        </div>

        {/* Requests Tab */}
        {activeTab === "requests" && (
          <>
            {/* Filter & Actions */}
            <div className="flex items-center justify-between mb-6">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-white border-slate-200 text-slate-900">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={fetchDashboard} className="text-slate-600 border-slate-200 hover:bg-slate-50">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => navigate("/hospital/backorders")} className="bg-blue-700 hover:bg-blue-800 text-white">
                  <Plus className="w-4 h-4 mr-2" /> New Request
                </Button>
              </div>
            </div>

            {/* Requests Grid */}
            {filteredRequests.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-slate-900 font-medium mb-2">No requests found</h3>
                <p className="text-slate-500 mb-4">
                  {statusFilter === "all" 
                    ? "You haven't submitted any backorder requests yet."
                    : `No requests with status "${STATUS_CONFIG[statusFilter]?.label}"`}
                </p>
                <Button onClick={() => navigate("/hospital/backorders")} className="bg-blue-700 hover:bg-blue-800 text-white">
                  Submit Your First Request
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    onView={setSelectedRequest}
                    onEdit={setEditingRequest}
                    onCancel={setCancellingRequest}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-slate-900 font-medium">Recent Activity</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {activityLog.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No activity yet</p>
                </div>
              ) : (
                activityLog.map((log, idx) => (
                  <div key={idx} className="p-4 flex items-start gap-4 hover:bg-slate-50">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-blue-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-900 text-sm">{log.details || log.action}</p>
                      <p className="text-slate-400 text-xs mt-1">
                        {log.user} • {new Date(log.timestamp).toLocaleString()}
                      </p>
                      {log.request_items && (
                        <p className="text-slate-500 text-xs mt-1">
                          Items: {log.request_items.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* DI Tanks Tab */}
        {activeTab === "di-tanks" && (
          <div className="space-y-6" data-testid="hospital-di-section">
            {/* Header with action */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Droplets className="w-5 h-5 text-cyan-600" /> DI Water Tank Exchange Program
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchDiData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => setDiFormOpen(!diFormOpen)} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="request-exchange-btn">
                  <Plus className="w-4 h-4 mr-2" /> Request Exchange
                </Button>
              </div>
            </div>

            {/* Exchange Request Form */}
            {diFormOpen && (
              <div className="bg-white border border-cyan-200 rounded-xl p-6 space-y-4" data-testid="di-exchange-form">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Send className="w-4 h-4 text-cyan-600" /> Request DI Tank Exchange
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Company / Organization</Label>
                    <Input value={hospital?.hospital_name || ""} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label>Site Department *</Label>
                    <Input
                      value={diForm.department}
                      onChange={e => setDiForm(f => ({ ...f, department: e.target.value }))}
                      placeholder="e.g. SPD, Lab, Dialysis, Pharmacy"
                      data-testid="di-form-department"
                    />
                  </div>
                  <div>
                    <Label>Service Type</Label>
                    <Select value={diForm.service_type} onValueChange={v => setDiForm(f => ({ ...f, service_type: v }))}>
                      <SelectTrigger data-testid="di-form-service-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New Installation</SelectItem>
                        <SelectItem value="exchange">Tank Exchange</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tank Type</Label>
                    <Select value={diForm.tank_type} onValueChange={v => setDiForm(f => ({ ...f, tank_type: v }))}>
                      <SelectTrigger data-testid="di-form-tank-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Standard DI", "Mixed Bed DI", "Cation", "Anion", "Carbon"].map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tank Size</Label>
                    <Select value={diForm.tank_size} onValueChange={v => setDiForm(f => ({ ...f, tank_size: v }))}>
                      <SelectTrigger data-testid="di-form-tank-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["0.5 cu. ft.", "1.0 cu. ft.", "1.6 cu. ft.", "2.0 cu. ft.", "3.6 cu. ft."].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Requested Date *</Label>
                    <Input
                      type="date"
                      value={diForm.requested_date}
                      onChange={e => setDiForm(f => ({ ...f, requested_date: e.target.value }))}
                      data-testid="di-form-date"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={diForm.notes}
                    onChange={e => setDiForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Additional details or special requirements..."
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setDiFormOpen(false)}>Cancel</Button>
                  <Button onClick={handleDiSubmit} disabled={diSubmitting} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="di-form-submit">
                    {diSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Request
                  </Button>
                </div>
              </div>
            )}

            {/* Current DI Deployments */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h4 className="text-slate-900 font-medium">Your Active DI Tanks</h4>
              </div>
              {diDeployments.length === 0 ? (
                <div className="p-12 text-center">
                  <Droplets className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No DI tanks currently deployed at your facility.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {diDeployments.map(dep => (
                    <div key={dep.id} className={`p-4 flex items-center justify-between ${dep.exchange_overdue ? "bg-red-50" : dep.exchange_urgent ? "bg-amber-50" : ""}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dep.exchange_overdue ? "bg-red-100" : dep.exchange_urgent ? "bg-amber-100" : "bg-cyan-100"}`}>
                          <Droplets className={`w-5 h-5 ${dep.exchange_overdue ? "text-red-600" : dep.exchange_urgent ? "text-amber-600" : "text-cyan-600"}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{dep.department}</p>
                          <p className="text-sm text-slate-500">{dep.tank_type} - {dep.tank_size}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Next Exchange</p>
                        <p className={`font-medium ${dep.exchange_overdue ? "text-red-600" : dep.exchange_urgent ? "text-amber-600" : "text-slate-900"}`}>
                          {dep.exchange_date}
                        </p>
                        {dep.exchange_overdue && (
                          <Badge className="bg-red-100 text-red-700 text-xs mt-1">Overdue {Math.abs(dep.days_until_exchange)}d</Badge>
                        )}
                        {!dep.exchange_overdue && dep.exchange_urgent && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs mt-1">{dep.days_until_exchange} days left</Badge>
                        )}
                        {!dep.exchange_overdue && !dep.exchange_urgent && dep.days_until_exchange != null && (
                          <span className="text-xs text-slate-400">{dep.days_until_exchange} days</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My Exchange Requests */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h4 className="text-slate-900 font-medium">Your Exchange Requests</h4>
              </div>
              {diRequests.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No exchange requests submitted yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {diRequests.map(req => (
                    <div key={req.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{req.department}</p>
                        <p className="text-sm text-slate-500">{req.tank_type} - {req.tank_size} | {req.service_type === "new" ? "New Install" : "Exchange"}</p>
                        <p className="text-xs text-slate-400 mt-1">Requested: {req.requested_date}</p>
                      </div>
                      <Badge className={
                        req.status === "pending" ? "bg-amber-100 text-amber-700" :
                        req.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                        req.status === "completed" ? "bg-green-100 text-green-700" :
                        "bg-slate-100 text-slate-700"
                      }>
                        {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Request Details</DialogTitle>
            <DialogDescription className="text-slate-500">
              Request #{selectedRequest?.id.substring(0, 8)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${STATUS_CONFIG[selectedRequest.status]?.color}`}>
                  {STATUS_CONFIG[selectedRequest.status]?.label}
                </span>
                {selectedRequest.priority === "high" && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 rounded-full text-xs">HIGH PRIORITY</span>
                )}
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-slate-900 font-medium mb-3">Items</h4>
                <div className="space-y-2">
                  {selectedRequest.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-slate-900">{item.item_name}</p>
                        {item.mfr_number && <p className="text-slate-500 text-xs">MFR: {item.mfr_number}</p>}
                      </div>
                      <span className="text-slate-500">Qty: {item.quantity} {item.quantity_type}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {selectedRequest.quotes?.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="text-emerald-700 font-medium mb-2">Quotes Received</h4>
                  <p className="text-slate-600 text-sm">
                    {selectedRequest.quotes.length} quote(s) available. Our team will contact you with pricing details.
                  </p>
                </div>
              )}
              
              {selectedRequest.notes && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-slate-900 font-medium mb-2">Notes</h4>
                  <p className="text-slate-600 text-sm">{selectedRequest.notes}</p>
                </div>
              )}
              
              <div className="text-slate-500 text-sm">
                Submitted: {new Date(selectedRequest.created_at).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog open={!!cancellingRequest} onOpenChange={() => setCancellingRequest(null)}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Cancel Request?</DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to cancel this request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancellingRequest(null)} className="border-slate-200">
              Keep Request
            </Button>
            <Button variant="destructive" onClick={handleCancelRequest}>
              Yes, Cancel Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Building2 className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm">AskIsaac |  | 800.988.3892</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
